import { z } from "zod";

const phoneRe = /^[+()\-\s0-9]{7,20}$/;

export const restaurantAddressSchema = z.object({
  formattedAddress: z.string().trim().min(1).max(512),
  address1: z.string().trim().max(255).nullish(),
  city: z.string().trim().max(120).nullish(),
  state: z.string().trim().max(120).nullish(),
  zipcode: z.string().trim().max(32).nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});
export type RestaurantAddressRequest = z.infer<typeof restaurantAddressSchema>;

export const restaurantRequestSchema = z.object({
  name: z.string().trim().min(1).max(160),
  type: z.string().trim().min(1).max(64),
  websiteUrl: z
    .string()
    .trim()
    .url()
    .max(1024)
    .optional()
    .or(z.literal("")),
  address: restaurantAddressSchema,
});
export type RestaurantRequest = z.infer<typeof restaurantRequestSchema>;

export const ownerContactRequestSchema = z.object({
  role: z.string().trim().min(1).max(64),
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().regex(phoneRe, "Enter a valid phone number"),
});
export type OwnerContactRequest = z.infer<typeof ownerContactRequestSchema>;

export const ownerOnboardingProfileRequestSchema = z.object({
  restaurant: restaurantRequestSchema.optional(),
  contact: ownerContactRequestSchema.optional(),
});
export type OwnerOnboardingProfileRequest = z.infer<
  typeof ownerOnboardingProfileRequestSchema
>;

export const termsAcceptanceRequestSchema = z.object({
  accepted: z.literal(true),
  version: z.string().min(1),
});
export type TermsAcceptanceRequest = z.infer<
  typeof termsAcceptanceRequestSchema
>;

export const restaurantAddressResponseSchema = z.object({
  formattedAddress: z.string().nullish(),
  address1: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zipcode: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});

export const restaurantResponseSchema = z.object({
  name: z.string().nullish(),
  type: z.string().nullish(),
  imageUrl: z.string().nullish(),
  websiteUrl: z.string().nullish(),
  address: restaurantAddressResponseSchema.nullish(),
});

export const ownerContactResponseSchema = z.object({
  role: z.string().nullish(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  photoUrl: z.string().nullish(),
});

/**
 * `configured` = a default payment method is saved. With subscription billing
 * `cardBrand` may be a bank name (ACH) — see BillingStatus.paymentMethod for the type.
 */
export const billingSummarySchema = z.object({
  configured: z.boolean(),
  cardBrand: z.string().nullish(),
  cardLast4: z.string().nullish(),
});

export const ownerOnboardingProfileResponseSchema = z.object({
  userId: z.string(),
  email: z.string().nullish(),
  restaurant: restaurantResponseSchema.nullable(),
  contact: ownerContactResponseSchema.nullable(),
  termsAccepted: z.boolean(),
  termsVersion: z.string().nullish(),
  billing: billingSummarySchema,
  status: z.enum(["DRAFT", "COMPLETE"]),
  updatedAt: z.string(),
});
export type OwnerOnboardingProfileResponse = z.infer<
  typeof ownerOnboardingProfileResponseSchema
>;

export const ownerOnboardingCompletionResponseSchema = z.object({
  profileComplete: z.boolean(),
  matchFeedPath: z.string().optional(),
  dashboardPath: z.string().optional(),
});
export type OwnerOnboardingCompletionResponse = z.infer<
  typeof ownerOnboardingCompletionResponseSchema
>;
